/**
 * MathGraph32 Javascript : Software for animating online dynamic mathematics figures
 * http://www.mathgraph32.org/
 * @Author Yves Biton (yves.biton@sesamath.net)
 * @License: GNU AGPLv3 https://www.gnu.org/licenses/agpl-3.0.html
 */
const NatObj = require('../types/NatObj')
const StyleEncadrement = require('../types/StyleEncadrement')
const CAffLiePt = require('./CAffLiePt')
const CLatex = require('./CLatex')
const CValeurAngle = require('./CValeurAngle')
module.exports = CMacro

/**
 * Classe ancêtre de toutes les macros.
 * @constructor
 * @extends CAffLiePt
 * @param {CListeObjets} listeProprietaire : La liste propriétaire.
 * @param {CImplementationProto} impProto : null ou la construction propriétaire.
 * @param {Boolean} estElementFinal : true si l'objet est un élément final de construction.
 * @param {mtg32.Color} couleur : La couleur d'éciture de l'éditeur (et du cadre éventuel).
 * @param {Float} x_nom : L'abscisse d'affichage de la macro
 * @param {Float} y_nom : L'ordonnée d'affichage de la macro
 * @param {Integer} dec_x : Décalage horizontal du nom
 * @param {Integer} dec_y : Décalage vertical du nom
 * @param {Boolean} masque : true si l'éditeur est masqué
 * @param {CPt} pointLie : null ou pointe sur un point auquel l'affichage est lié.
 * @param {Integer} taillePolice : Indice de la taiile de police utilisée
 * @param {Boolean} effacementFond : true si on efface le fond de l'en-tête.
 * @param {mtg32.Color} couleurFond : La couleur de fond de l'en-tête.
 * @param {Integer} alignementHorizontal : 0 pour alignement gauche, 1 pour centre, 2 pour droite.
 * @param {Integer} alignementVertical : 0 pour alignement vers le haut, 1 pour centré, 2 pour bas.
 * @param {String} intitule : Le titre de la macro
 * @param {String} commentaireMacro : Eventuel commentaire explicatif.
 * @returns {CMacro}
 */
function CMacro (listeProprietaire, impProto, estElementFinal, couleur,x_nom, y_nom,
    dec_x, dec_y, masque, pointLie, taillePolice, effacementFond, couleurFond, alignementHorizontal,
    alignementVertical, intitule, commentaireMacro) {
  if (arguments.length !== 0) {
    if (arguments.length === 1) CAffLiePt.call(this, listeProprietaire);
    else {
      CAffLiePt.call(this, listeProprietaire, impProto, estElementFinal, couleur,
        x_nom, y_nom,dec_x, dec_y, masque, pointLie, taillePolice, StyleEncadrement.Effet3D,
        effacementFond, couleurFond, alignementHorizontal, alignementVertical,
        new CValeurAngle(listeProprietaire, 0));
      this.intitule = intitule;
      this.commentaireMacro = commentaireMacro;
      this.macroLanceuse = null; // Ajout version 5.1
    }
    this.executionEnCours = false;
  }
}
CMacro.prototype = new CAffLiePt();
CMacro.prototype.constructor = CMacro;
CMacro.prototype.superClass = "CAffLiePt";
CMacro.prototype.className = "CMacro";

CMacro.sansAnimation = 0;
CMacro.animationParTimer = 1;
CMacro.animationParThread = 2;

CMacro.prototype.getNature = function() {
  return NatObj.NMacro;
};
CMacro.prototype.execute = function() {
  this.executionEnCours = true;
};
// Ajout version 5.1
/**
 * Fonction servant à informer une macro qu'elle est lancée par une macro
 * mac qui est nécessairement une CMacroSuiteMacro
 * Sert lors de l'éxécution d'une macro enchaînant d'autres macros qui peuvent elles
 * mêmes être des macros enchaînant d'autres macros.
 * @param {type} mac
 * @returns {undefined}
 */
CMacro.prototype.setMacroLanceuse = function(mac) {
  this.macroLanceuse = mac;
};
CMacro.prototype.termineAction = function(svg, dimf, couleurFond, transparence) {
  this.executionEnCours = false;
  /* Modifié version 5.1
  var mac = this.listeProprietaire.macroEnCours;
  if ((mac !== null) && (mac.className === "CMacroSuiteMacros")) {
    this.listeProprietaire .macroEnCours.passageMacroSuiv(svg, dimf, couleurFond, transparence);
  }
  else this.listeProprietaire.macroEnCours = null;
  */
  if (this.macroLanceuse !== null) {
    this.macroLanceuse.passageMacroSuiv(svg, dimf, couleurFond, transparence);
  }
  else this.listeProprietaire.macroEnCours = null;
};
CMacro.prototype.passageMacroSuiv = function(svg, dimf, couleurFond, transparence) {
  /* Modification version 5.1
  if (this.listeProprietaire.macroEnCours.className === "CMacroSuiteMacros") {
    this.listeProprietaire.macroEnCours.passageMacroSuiv(svg, dimf, couleurFond, transparence);
  }
  else this.listeProprietaire.macroEnCours = null;
  */
  if (this.macroLanceuse === null) {
    this.listeProprietaire.macroEnCours = null;
  }
  else {
    this.macroLanceuse.passageMacroSuiv(svg, dimf, couleurFond, transparence);
  }
};
/**
 * Fonctions à définir pour les descendants remettant à jour les listes
 * utilisées par la macro.
 * @returns {undefined}
 */
CMacro.prototype.etablitListesInternes = function() {
};
/* Pas utlisé pour le moment
CMacro.prototype.trace = function(svg, couleurFond, transparence) {
  // Les macros qui sont masquées ne doivent pas êre affichées quand on active l'iutil rideau
  // de démasquage d'objet
  if (this.masque) return;
  CAffLiePt.prototype.trace.call(this, svg, couleurFond, transparence);
}
*/
CMacro.prototype.rendChaineAffichage = function() {
  var len = this.intitule.length;
  if (this.intitule.charAt(0) === "$") {
    if (this.intitule.charAt(this.len-1) === "$") {
      return "$\\Rightarrow " + this.intitule.substring(1, len-1) + "$";
    }
  }
  return "⇒" + this.intitule;
};
/**
 * MacroEnCours renvoie this excepté pour les macros de type CMacroSuiteMacro
 * @returns {CMacro.prototype}
 */
CMacro.prototype.macroEnCours = function() {
  return this;
};
/**
 * envoie le type d'animation utilisé par la macro quand elle est exécutée.
 * Voir les types définis dans les premières lignes.
 * @returns {Number}
 */
CMacro.prototype.typeAnimation = function() {
  return CMacro.sansAnimation;
};
/**
 * Renvoie la fréquence d'animation pour les macros d'animation.
 * Doit être redéfini pour les macros utilisant une animation
 * @returns {Number}
 */
CMacro.prototype.frequenceAnimation = function() {
  return 0;
};
/**
 * Renvoie true si l'exécution de la macro est possible.
 * @returns {Boolean}
 */
CMacro.prototype.executionPossible = function() {
  return true;
};
/**
 * Fonction renvoyant true si la macro est prévue pour être arrêtée par un clic de souris.
 * Si la réponse est true et si la macro est contneue dans une macro exécutant une suite de macros
 * un clic de souris fera passer à la macro suivante.
 * @returns {Boolean}
 */
CMacro.prototype.arretParClic = function() {
  return false;
};
/**
 * Renvoie faux par défaut. A redéfinir pour les descendants agissant sur une variable.
 * @param {type} va
 * @returns {Boolean}
 */
CMacro.prototype.agitSurVariable = function(va) {
  return false;
};
/**
 * Initialise la macro.
 * @returns {undefined}
 */
CMacro.prototype.initialise = function() {
  // Ajout version 5.1
  this.listeProprietaire.macroEnCours = this;
  //
};
/**
 * Renvoie true si la macro affecte val ou un calcul dépendant de val à une variable.
 * A redéfinir pour les macros d'affectation de valeur à variable, de modification de variable
 * et d'exécution d'une suite de macros
 * @param {type} val
 * @returns {Boolean}
 */
CMacro.prototype.affecteValeurAVariable = function(val) {
  return false;
};
/**
 * Fonction à redéfinir pour les macros pouvant être exécutées en début ou fin de boucle
 * pour les macros de boucle. Doit exécuter la macro mais sans action graphique sur la figure.
 * @param {type} dimf
 * @returns {undefined}
 */
CMacro.prototype.executeSimple = function(dimf) {
};
CMacro.prototype.chaineDesignation = function() {
  return "desMacro";
};
CMacro.prototype.read = function(inps, list){
  CAffLiePt.prototype.read.call(this, inps, list);
  this.intitule = CLatex.traiteAccents(inps.readUTF());
  this.commentaireMacro = inps.readUTF();
  this.macroLanceuse = null; // Ajout version 5.1
};
CMacro.prototype.write = function(oups, list){
  CAffLiePt.prototype.write.call(this, oups, list);
  oups.writeUTF(this.intitule);
  oups.writeUTF(this.commentaireMacro);
};
